import React, { useEffect, useState } from "react";
import { adminListConsultations, adminUpdateConsultation } from "@/lib/api";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Calendar, Clock, Mail, Phone } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["requested", "confirmed", "completed", "cancelled"];
const STATUS_COLOR = {
    requested: "bg-brand-accent/20 text-brand-accent border-brand-accent/40",
    confirmed: "bg-[#3E7A5D]/20 text-[#3E7A5D] border-[#3E7A5D]/40",
    completed: "bg-brand-primary/15 text-brand-primary border-brand-primary/40",
    cancelled: "bg-[#9A4040]/15 text-[#9A4040] border-[#9A4040]/40",
};

export default function AdminConsultations() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            setItems(await adminListConsultations());
        } catch {
            toast.error("Could not load consultations");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        load();
    }, []);

    const update = async (c, status) => {
        try {
            const updated = await adminUpdateConsultation(c.id, { status });
            setItems((arr) => arr.map((x) => (x.id === c.id ? updated : x)));
            toast.success(`Marked as ${status}`);
        } catch {
            toast.error("Update failed");
        }
    };

    return (
        <div data-testid="admin-consultations" className="space-y-6">
            <div>
                <div className="text-xs uppercase tracking-[0.22em] text-brand-accent font-bold">
                    Diary
                </div>
                <h1 className="font-serif-editorial text-4xl text-brand-primary mt-1">
                    Consultations
                </h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {loading ? (
                    <div className="text-brand-muted">Loading…</div>
                ) : items.length === 0 ? (
                    <div className="text-brand-muted">No consultations requested yet.</div>
                ) : (
                    items.map((c) => (
                        <div
                            key={c.id}
                            data-testid={`consult-card-${c.id}`}
                            className="bg-brand-surface border border-brand-border p-6"
                        >
                            <div className="flex items-start justify-between">
                                <div>
                                    <div className="font-bold text-brand-primary text-lg">
                                        {c.name}
                                    </div>
                                    <div className="text-xs text-brand-muted mt-1">
                                        Requested {new Date(c.created_at).toLocaleString()}
                                    </div>
                                </div>
                                <Select value={c.status} onValueChange={(v) => update(c, v)}>
                                    <SelectTrigger
                                        data-testid={`consult-status-${c.id}`}
                                        className={`h-8 text-[11px] font-bold uppercase tracking-wide border ${STATUS_COLOR[c.status] || STATUS_COLOR.requested}`}
                                    >
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {STATUSES.map((s) => (
                                            <SelectItem key={s} value={s}>{s}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>

                            <div className="mt-5 grid grid-cols-2 gap-3 text-sm text-brand-muted">
                                <div className="flex items-center gap-2">
                                    <Calendar size={14} className="text-brand-accent" />
                                    {c.preferred_date}
                                </div>
                                <div className="flex items-center gap-2">
                                    <Clock size={14} className="text-brand-accent" />
                                    {c.preferred_time}
                                </div>
                                <a
                                    href={`mailto:${c.email}`}
                                    className="flex items-center gap-2 hover:text-brand-primary"
                                >
                                    <Mail size={14} /> {c.email}
                                </a>
                                {c.phone && (
                                    <a
                                        href={`tel:${c.phone}`}
                                        className="flex items-center gap-2 hover:text-brand-primary"
                                    >
                                        <Phone size={14} /> {c.phone}
                                    </a>
                                )}
                            </div>

                            {(c.topic || c.notes) && (
                                <div className="mt-5 pt-5 border-t border-brand-border space-y-3 text-sm">
                                    {c.topic && (
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">Topic</div>
                                            <div className="text-brand-primary mt-1">{c.topic}</div>
                                        </div>
                                    )}
                                    {c.notes && (
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.2em] text-brand-muted font-bold">Notes</div>
                                            <div className="text-brand-primary mt-1">{c.notes}</div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))
                )}
            </div>
        </div>
    );
}
